import { useState } from "react";

import { generateNoticeSearchUrl } from "components/notices/utils";
import toast from "components/ui/toast/Toast";
import { useAddNoticeToList } from "../../../../lib/hooks/api/notices/lists/useAddNoticeToList";
import { useCreateNoticeList } from "../../../../lib/hooks/api/notices/lists/useCreateNoticeList";
import { useNoticeLists } from "../../../../lib/hooks/api/notices/lists/useNoticeLists";
import { useNoticeListsForRecord } from "../../../../lib/hooks/api/notices/lists/useNoticeListsForRecord";
import { useRemoveNoticeFromList } from "../../../../lib/hooks/api/notices/lists/useRemoveNoticeFromList";
import { ListForm } from "../../../user_lists/UserListsModals";
import { ToastAction } from "../../ToastAction";
import { CreateListFormProps, LoadingState, SaveToList } from "./SaveToList";

export function SaveToNoticeList({
  entityId,
  isCreateFormOpen,
  onToggleCreateForm,
}: {
  entityId: string;
  isCreateFormOpen: boolean;
  onToggleCreateForm: (isCreateFormOpen: boolean) => void;
}) {
  const { data: lists, isLoading, isError } = useNoticeLists();
  const {
    data: savedNoticeLists,
    isLoading: isLoadingSaved,
    isError: isSavedError,
  } = useNoticeListsForRecord(entityId);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    lists: isLoading,
    itemId: undefined,
  });

  // TODO: add missing params
  const tracking = { contextSource: "Save drawer", signals: [], recordStage: "" };
  // A failed request has no response, so its toast names the list from the company's lists.
  const listName = (listId: number) => lists?.find((x) => x.id === listId)?.name;

  const { mutate: addToList } = useAddNoticeToList(tracking, {
    onSuccess: ({ addNoticeToList: list }) => {
      setLoadingState((prev) => ({ ...prev, itemId: undefined }));
      toast.message(`Notice saved to ${list.name}`, {
        action: (
          <ToastAction
            objectType="list"
            url={generateNoticeSearchUrl({ noticeLists: [list.id.toString()] })}
          />
        ),
      });
    },
    onError: (_, { input }) => {
      setLoadingState((prev) => ({ ...prev, itemId: undefined }));
      toast.error(`Failed to save notice to ${listName(input.listId)}`);
    },
  });

  const { mutate: removeFromList } = useRemoveNoticeFromList(tracking, {
    onSuccess: ({ removeNoticeFromList: list }) => {
      setLoadingState((prev) => ({ ...prev, itemId: undefined }));
      toast.message(`Notice removed from ${list.name}`, {
        action: (
          <ToastAction
            objectType="list"
            url={generateNoticeSearchUrl({ noticeLists: [list.id.toString()] })}
          />
        ),
      });
    },
    onError: (_, { input }) => {
      setLoadingState((prev) => ({ ...prev, itemId: undefined }));
      toast.error(`Failed to remove notice from ${listName(input.listId)}`);
    },
  });

  const [searchValue, setSearchValue] = useState("");

  const items =
    lists?.map((list) => ({
      id: list.guid,
      label: list.name,
      isSaved: savedNoticeLists?.some((x) => x.guid === list.guid) ?? false,
      viewUrl: generateNoticeSearchUrl({ noticeLists: [list.id.toString()] }),
    })) ?? [];
  const filteredItems = searchValue
    ? items.filter((item) => item.label.toLowerCase().includes(searchValue.toLowerCase()))
    : items;

  const onItemClick = (id: string, isSaved: boolean) => {
    const list = lists?.find((x) => x.guid === id);
    if (!list) {
      return;
    }

    setLoadingState((prev) => ({ ...prev, itemId: id }));

    const input = { listId: list.id, noticeGuid: entityId };
    if (isSaved) {
      removeFromList({ input });
    } else {
      addToList({ input });
    }
  };

  return (
    <SaveToList
      items={filteredItems}
      loading={{
        // Wait for the notice's own lists too, so a saved list never flashes as unsaved.
        lists: isLoading || isLoadingSaved,
        itemId: loadingState.itemId,
      }}
      // Without the notice's own lists we can't tell saved from unsaved, so don't offer either.
      isError={isError || isSavedError}
      onItemClick={onItemClick}
      entityIds={[entityId]}
      entityType="notice"
      onSearch={(text) => setSearchValue(text)}
      CreateListForm={CreateNoticeList}
      isCreateFormOpen={isCreateFormOpen}
      onToggleCreateForm={onToggleCreateForm}
    />
  );
}

function CreateNoticeList(props: CreateListFormProps) {
  const { mutate: createList } = useCreateNoticeList({
    onSuccess: ({ createNoticeList: list }) => {
      toast.message(`Notice saved to ${list.name}`, {
        action: (
          <ToastAction
            objectType="list"
            url={generateNoticeSearchUrl({ noticeLists: [list.id.toString()] })}
          />
        ),
      });
      props.onSuccess();
    },
    onError: () =>
      toast.error("Unable to create your list. Please contact us if the problem persists"),
  });

  return (
    <ListForm
      {...props}
      listName=""
      onSubmit={(newList) =>
        createList({ input: { name: newList.name, noticeGuids: props.entityIds } })
      }
      isSubmitting={false}
    />
  );
}
